import { Brush } from 'three-bvh-csg';
import { Color } from 'three/src/math/Color';
import { Mesh } from 'three/src/objects/Mesh';
import { PIPE_COLOURS } from 'modules/project/ProjectAreaPage/ProjectStopesPage/MineModel/GConstants';
import {
  defaultFillAnchor,
  dischargeFloorY,
  FillAnchor,
  getGlobalMinY,
  meshVolume
} from './stopeGeometry';
import {
  BeachSurface, dischargeDatum, FillCurve, getFillCurve, surfaceHeightAt
} from './fillSurface';
import { buildSliceSolid } from './fillSolids';

/**
 * The stope-fill stacking kernel.
 *
 * THE MODEL. The fill is the pointwise MAXIMUM of every pour's cone of repose - paste piles on whatever
 * is already there, and nothing already placed ever moves. A pour is therefore fully described by one
 * BeachSurface (apex level, discharge anchor, beach angle), and the state carried forward is the LIST of
 * them, not a single level and a running volume.
 *
 * WHAT EACH INPUT MODE HOLDS FIXED:
 *   - HEIGHT mode fixes the height of the cone. Its apex is `dischargeDatum + pourHeight`, and the datum
 *     is the fill surface directly under this pour's own pipe (the floor where nothing has reached it).
 *     Neither depends on the pour's beach angle, so changing the angle widens or narrows the cone without
 *     moving its peak. The VOLUME is what gives.
 *   - VOLUME mode fixes the volume. The apex is solved for, so moving the pipe or changing the angle
 *     changes the HEIGHT instead.
 *
 * TWO SEPARATE PIPELINES, and it matters which is which:
 *   - VOLUMES are AUTHORITATIVE and come from the exact integral (fillSurface). A slice is
 *     `volumeUnder(everything including this pour) - volumeUnder(everything before it)`, so slices
 *     telescope: they sum EXACTLY to the filled volume with no drift, and none can be negative.
 *   - DISPLAY SOLIDS are ILLUSTRATIVE and come from CSG (fillSolids), always cut from the PRISTINE
 *     repaired stope so boolean error cannot compound from one pour to the next.
 *
 * THE DATUM. Pour 1's height is measured from the floor of the solid directly under the discharge pipe,
 * probed ONCE against the pristine repaired mesh - NOT from the level at which the enclosed volume is
 * zero. On real stopes the V = 0 level sits 4.9-5.7 m below the pipe floor because of a drawpoint or
 * undercut lobe, so a 5 m first pour measured from there would top out 5 m above the LOBE floor rather
 * than 5 m above the pipe floor and shrink by 60-93%. Measured on stope 01jv9w5nnj (pipe floor 5.50 m,
 * V = 0 at 0.06 m): 1181 m3 from the pipe floor against 426 m3 from V = 0.
 *
 * VOLUME ATTRIBUTION. Nothing is placed before pour 1, so its base volume is 0 and it is credited with
 * EVERYTHING below its beach, drawpoint lobe included. That is deliberate: it matches what the previous
 * implementations reported, and it keeps the books straight, with no orphaned remainder belonging to no
 * pour.
 *
 * THIS REPLACES a single-cone model that carried only a cumulative volume forward; fillSurface's header
 * has the argument and the measurements.
 */

// The hardcoded beach the stope fill view has always drawn. Mirrors Project.LegacyPasteBeachAngleDegrees
// on the backend.
export const LEGACY_BEACH_ANGLE_DEGREES = 1.25;

export const distinctColors = PIPE_COLOURS.map((hex) => new Color(hex));

export type PourColorMode = 'by-pour' | 'by-recipe';

/**
 * The colour each pour's shell is drawn in. `by-recipe` puts every pour sharing a recipe on one colour;
 * `by-pour` gives each its own.
 *
 * Keyed by entityId, because that is the only stable identity a pour has here - a display shell is a
 * fresh CSG Brush whose Object3D uuid is random and unrelated to it.
 *
 * Every pour gets a colour, which the two branches did not previously guarantee:
 *   - The palette WRAPS. PIPE_COLOURS holds 22 entries, and indexing past the end returns undefined; a
 *     stope with more pours (presets generate them in bulk) or more distinct recipes than that left the
 *     shell with no material of its own, which means it inherits the stope's and disappears into it.
 *   - A recipe-less pour - rock fill is created without one - has nothing to colour by in `by-recipe`
 *     mode, so it falls back to its own per-pour colour rather than to undefined.
 */
export const buildPourColors = <T extends { entityId: string; recipeId?: string | null }>(
  pours: T[],
  mode: PourColorMode,
): Map<string, Color> => {
  const colourAt = (index: number): Color => distinctColors[index % distinctColors.length];

  if (mode === 'by-pour') {
    return new Map(pours.map((pour, index) => [pour.entityId, colourAt(index)]));
  }

  const recipeIds = Array.from(new Set(pours.map((pour) => pour.recipeId).filter((id): id is string => !!id)));
  const byRecipe = new Map(recipeIds.map((id, index) => [id, colourAt(index)]));

  return new Map(pours.map((pour, index) => [
    pour.entityId,
    (pour.recipeId ? byRecipe.get(pour.recipeId) : undefined) ?? colourAt(index),
  ]));
};

/**
 * The beach surface slope of an ALREADY-PERSISTED pour, in degrees from horizontal.
 *
 * The project default is deliberately NOT consulted here, and the parameter to pass it does not exist so
 * that it cannot be. Every construction path stamps a concrete angle on the pour server-side, so the only
 * pour that can reach the fallback is one written before the field existed - and that pour was planned at
 * 1.25 by definition. Feeding the live project default in instead would re-slope historical pours the
 * moment a project setting changed, which flows straight through walkStopeFill into "Update Volumes" and
 * PATCHes recomputed numbers over the stored ones.
 *
 * For a pour that does not exist yet - a preview, a preset slot, an auto pour - use
 * resolveNewPourBeachAngle instead.
 *
 * `isRockFill` is checked FIRST and deliberately so: a rock-fill pour created before this field existed
 * has a null angle, and falling through to the paste default would silently turn a level rock fill into
 * a sloped paste beach.
 */
export const resolvePourBeachAngle = (
  pour: { isRockFill?: boolean; pasteBeachAngleDegrees?: number | null },
): number => (pour.isRockFill ? 0 : (pour.pasteBeachAngleDegrees ?? LEGACY_BEACH_ANGLE_DEGREES));

/**
 * The beach surface slope to PLAN A NEW POUR at: explicit override -> project default -> 1.25 constant,
 * with rock fill short-circuiting to a level 0.
 *
 * Kept separate from resolvePourBeachAngle so the two questions - "what was this pour planned at?" and
 * "what should the next pour be planned at?" - cannot be confused at a call site.
 */
export const resolveNewPourBeachAngle = (
  overrideDegrees?: number | null,
  projectDefaultDegrees?: number | null,
  isRockFill?: boolean,
): number => (isRockFill ? 0 : (overrideDegrees ?? projectDefaultDegrees ?? LEGACY_BEACH_ANGLE_DEGREES));

/** The pour fields the stacking walk needs. PourDto is structurally assignable to this. */
export interface StopeFillPour {
  entityId: string;
  intendedPourDate?: unknown;
  pourPositionX?: number | null;
  pourPositionY?: number | null;
  pourHeight?: number | null;
  volume: number;
  isRockFill?: boolean;
  pasteBeachAngleDegrees?: number | null;
}

export interface StopeFillStep<T extends StopeFillPour = StopeFillPour> {
  pour: T;
  index: number;
  /** Display only - what this pour's cone adds on top of everything below it, cut from the pristine mesh. */
  fillSolid: Brush;
  /** EXACT: filled volume including this pour minus filled volume before it. Never negative. */
  sliceVolume: number;
  /** The elevation this pour was measured from: the fill surface under its own pipe. */
  datum: number;
  /** This pour's beach. Its `level` is the apex, which at the anchor is an elevation. */
  surface: BeachSurface;
  /** `surface.level - datum`, i.e. the rise at the discharge point. Equals pourHeight in height mode. */
  riseAtDischarge: number;
  /** Cumulative filled volume INCLUDING this slice. */
  cumulativeVolume: number;
  /** The beach slope this pour was planned at: its own stored angle, or 1.25 if it predates the field. */
  beachAngleDegrees: number;
}

export interface StopeFillWalk {
  pristineStope: Mesh;
  totalVolume: number;
  cumulativeVolume: number;
  /** Every beach placed, in order. The fill surface is their pointwise maximum. */
  surfaces: BeachSurface[];
  /** The floor of the solid under the FIRST pour's pipe - the datum pour 1 was measured from. */
  dischargeFloorLevel: number;
  pourCount: number;
}

const pourDateString = (pour: { intendedPourDate?: unknown }): string => {
  const date = pour.intendedPourDate as { toString?: () => string } | undefined;
  return date?.toString?.() ?? '';
};

// Pours must be stacked bottom-up (chronologically); the query order is not guaranteed, so sort by the
// intended pour date string. Shared so every consumer of walkStopeFill orders pours identically.
export const sortPoursChronologically = <T extends { intendedPourDate?: unknown }>(pours: T[]): T[] => (
  [...pours].sort((a, b) => pourDateString(a).localeCompare(pourDateString(b)))
);

const pourAnchor = <T extends StopeFillPour>(pour: T, defaultAnchor: FillAnchor): FillAnchor => (
  pour.pourPositionX != null && pour.pourPositionY != null
    ? { x: pour.pourPositionX, z: pour.pourPositionY }
    : defaultAnchor
);

export interface NextSliceBase {
  curve: FillCurve;
  /** The elevation this pour's height is measured from. */
  datum: number;
  /** The volume already in the stope, under everything placed before this pour. */
  baseVolume: number;
}

/**
 * Everything needed to place one more pour on top of `placed`, for a pour discharging at `anchor` with
 * `slopeDegrees`. This is the SAME derivation walkStopeFill applies when that pour is committed, which is
 * what makes a preview identical to the pour it becomes.
 *
 *   height -> volume:  curve.volumeAt(datum + height) - baseVolume
 *   volume -> height:  curve.levelAt(baseVolume + volume) - datum
 */
export const nextSliceBase = (
  pristineStope: Mesh,
  anchor: FillAnchor,
  slopeDegrees: number,
  placed: readonly BeachSurface[],
): NextSliceBase => {
  const curve = getFillCurve(pristineStope, placed, anchor, slopeDegrees);
  return {
    curve,
    datum: dischargeDatum(pristineStope, placed, anchor),
    baseVolume: curve.baseVolume,
  };
};

/**
 * The single source of truth for stacking a stope's pours.
 *
 * Each pour adds its own cone on top of everything already placed. The slice volume telescopes out of the
 * running total, so it is exact and non-negative however far the pipe moves or the angle changes between
 * pours - which is precisely what the model this replaces could not do.
 *
 * `pours` must already be ordered (see sortPoursChronologically). `visit` is called once per slice.
 */
export const walkStopeFill = <T extends StopeFillPour>(
  pristineStope: Mesh,
  pours: T[],
  defaultAnchor: FillAnchor,
  visit: (step: StopeFillStep<T>) => void,
): StopeFillWalk => {
  const firstPour = pours[0];
  const datumAnchor = firstPour ? pourAnchor(firstPour, defaultAnchor) : defaultAnchor;
  const dischargeFloorLevel = dischargeFloorY(pristineStope, datumAnchor);

  const placed: BeachSurface[] = [];
  let cumulativeVolume = 0;
  const totalVolume = meshVolume(pristineStope);

  pours.forEach((pour, index) => {
    const anchor = pourAnchor(pour, defaultAnchor);
    // Every pour here already exists, so each is stacked at the angle it was PLANNED at. The project
    // default must not reach this walk - see resolvePourBeachAngle.
    const slopeDegrees = resolvePourBeachAngle(pour);
    const curve = getFillCurve(pristineStope, placed, anchor, slopeDegrees);
    const datum = dischargeDatum(pristineStope, placed, anchor);

    const level = pour.pourHeight != null
      ? datum + pour.pourHeight
      : curve.levelAt(curve.baseVolume + pour.volume);

    const sliceVolume = pour.pourHeight != null
      ? Math.max(0, curve.volumeAt(level) - curve.baseVolume)
      : pour.volume;

    const surface: BeachSurface = { level, anchor, slopeDegrees };
    const fillSolid = buildSliceSolid(pristineStope, placed, surface);

    cumulativeVolume += sliceVolume;
    placed.push(surface);

    visit({
      pour,
      index,
      fillSolid,
      sliceVolume,
      datum,
      surface,
      riseAtDischarge: level - datum,
      cumulativeVolume,
      beachAngleDegrees: slopeDegrees,
    });
  });

  return {
    pristineStope,
    totalVolume,
    cumulativeVolume,
    surfaces: placed,
    dischargeFloorLevel,
    pourCount: pours.length,
  };
};

export {
  defaultFillAnchor,
  dischargeDatum,
  dischargeFloorY,
  getFillCurve,
  getGlobalMinY,
  meshVolume,
  surfaceHeightAt,
};
export type {
  BeachSurface, FillAnchor, FillCurve,
};
